import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'
import { Loader, Save } from 'lucide-react'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { BillingDetailsForm } from '@/components/form/billing-details'
import { billingDetailsSchema } from '@/components/form/billing-details/schema'
import {
  ONBOARDING_BILLING_QUERY,
  ONBOARDING_UPDATE_STRIPE_ADDRESS,
} from '@/components/payment-method-gate/gql'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H2 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Form } from '@/components/ui/form'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  OnboardingBillingDetailsQuery,
  OnboardingBillingDetailsQueryVariables,
  OnboardingUpdateStripeAddressMutation,
  OnboardingUpdateStripeAddressMutationVariables,
} from '@/components/payment-method-gate/gql.types.gen'
import type { z } from 'zod/v4'

type BillingOnlyFormValues = z.input<typeof billingDetailsSchema>

type Props = {
  isSuperadmin: boolean
  onSkip: () => void
  onSuccess: () => void
}

export const BillingOnlyWizard = ({
  isSuperadmin,
  onSkip,
  onSuccess,
}: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()

  const { data } = useSuspenseQuery<
    OnboardingBillingDetailsQuery,
    OnboardingBillingDetailsQueryVariables
  >(ONBOARDING_BILLING_QUERY, { variables: { idCompany } })

  const [updateStripeAddress] = useMutation<
    OnboardingUpdateStripeAddressMutation,
    OnboardingUpdateStripeAddressMutationVariables
  >(ONBOARDING_UPDATE_STRIPE_ADDRESS, {
    refetchQueries: ['CompanyBilling'],
  })

  const stripeAddress = data?.company?.sevallaBilling?.address

  const form = useForm<BillingOnlyFormValues>({
    resolver: zodResolver(billingDetailsSchema),
    defaultValues: {
      name: stripeAddress?.name ?? '',
      country: stripeAddress?.country ?? '',
      state: stripeAddress?.state ?? '',
      city: stripeAddress?.city ?? '',
      zip: stripeAddress?.postalCode ?? '',
      street: stripeAddress?.line1 ?? '',
      street2: stripeAddress?.line2 ?? '',
    },
  })

  const onSubmit = async (values: BillingOnlyFormValues) => {
    if (isPending) {
      return
    }

    startTransition(async () => {
      try {
        await updateStripeAddress({
          variables: {
            idCompany,
            address: {
              name: values.name,
              city: values.city,
              country: values.country,
              line1: values.street,
              line2: values.street2 ?? '',
              postalCode: values.zip,
              state: values.state,
            },
          },
        })
        toast(
          <ToasterContent variant="success">
            Billing details saved.
          </ToasterContent>,
          { position: 'top-right' }
        )
        onSuccess()
      } catch (e) {
        Sentry.captureException(e)
        toast(
          <ToasterContent variant="error">
            Failed to save billing details. Please try again.
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <div className="pb-25">
      <Card className="py-0 overflow-hidden">
        <div className="p-6 lg:p-8">
          <div className="mb-8">
            <H2 className="text-pretty">Billing details required</H2>
            <p className="mt-2 text-muted-foreground text-pretty">
              We need valid billing details on file to keep your account
              compliant with tax and invoicing regulations. These details appear
              on every invoice we issue.
            </p>
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <BillingDetailsForm isDisabled={isPending} />
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={isPending}
              >
                {isPending ? (
                  <Loader className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save />
                )}
                Save billing details
              </Button>
            </form>
          </Form>
          {isSuperadmin ? (
            <div className="mt-6 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={onSkip}
              >
                Skip (superadmin)
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  )
}
